import { SamplePointsState } from 'store/modules/samplePoints/types';
import { RawAssetTypeCode } from 'types/models/asset-type';
import SamplePoint, { RawSamplePoint, SensorTagId } from 'types/models/samplePoint';
import { powerMeterSamplePointReducer } from 'utils/associated-sample-points/merge-power-meters';
import { safetyCheckInSamplePointReducer } from 'utils/associated-sample-points/merge-safety-check-in-sos';
import { soilSamplePointReducer } from 'utils/associated-sample-points/merge-soil-moisture-temperature';
import parseRawSamplePoint from 'utils/associated-sample-points/parse-raw-samplepoint';

import { checkIsHeaterHost } from '../check-is-heater-host';
import { heaterSamplePointsReducer } from '../merge-heater-sample-points';
import { machineControlSamplePointReducer } from '../merge-machine-controls';
import { weatherStationSamplePointReducer } from '../merge-weather-station-sample-points';

/**
 * Takes an array of raw samplePoints and returns an array of parsed
 * samplePoints. SamplePoints of unsupported asset types are removed.
 */
export const parseRawSamplePoints = (
  rawSamplePoints: RawSamplePoint[],
  country: string,
  existingSamplePointsState?: SamplePointsState
): SamplePoint[] => {
  // To make sure the main sample points, during merging, are always in front of other sample points, we sort the array
  // first. It simplifies the merging logic.
  const sortedRawSamplePoints = rawSamplePoints
    .sort((a, b) => {
      // Weather station controllers first so child sensors can merge into them during the .reduce() below.
      const isWeatherStationController = (assetTypeId: RawAssetTypeCode) =>
        assetTypeId === RawAssetTypeCode.WEATHER_STATION_CONTROLLER;
      if (isWeatherStationController(a.assetTypeId) && !isWeatherStationController(b.assetTypeId)) return -1;
      if (isWeatherStationController(b.assetTypeId) && !isWeatherStationController(a.assetTypeId)) return 1;

      // Heater hosts first so child sensors can merge into them during the .reduce() below.
      if (checkIsHeaterHost(a) && !checkIsHeaterHost(b)) return -1;
      if (checkIsHeaterHost(b) && !checkIsHeaterHost(a)) return 1;

      return 0;
    });

  return sortedRawSamplePoints
    .reduce<SamplePoint[]>((acc, rawSP) => {
      switch (rawSP.assetTypeId) {
        case RawAssetTypeCode.SOIL_MOISTURE:
        case RawAssetTypeCode.SOIL_TEMP:
          return soilSamplePointReducer(acc, rawSP);
        case RawAssetTypeCode.SAFETY_CHECK_IN_SOS:
        case RawAssetTypeCode.SAFETY_CHECK_IN_VISIT:
          return safetyCheckInSamplePointReducer(acc, rawSP);
        case RawAssetTypeCode.MACHINE_CONTROL:
          return machineControlSamplePointReducer(acc, rawSP);
        case RawAssetTypeCode.POWER_METER_FLAGS:
        case RawAssetTypeCode.POWER_METER_VOLTAGE:
        case RawAssetTypeCode.POWER_METER_CURRENT:
        case RawAssetTypeCode.POWER_METER_ENERGY:
          return powerMeterSamplePointReducer(acc, rawSP);
        case RawAssetTypeCode.BAROMETRIC_PRESSURE:
        case RawAssetTypeCode.DELTA_T:
        case RawAssetTypeCode.DEW_POINT:
        case RawAssetTypeCode.EVAPOTRANSPIRATION:
        case RawAssetTypeCode.HUMIDITY:
        case RawAssetTypeCode.SOLAR_RADIATION:
        case RawAssetTypeCode.TEMPERATURE:
        case RawAssetTypeCode.WEATHER_STATION_BATTERY:
        case RawAssetTypeCode.WEATHER_STATION_CONTROLLER:
        case RawAssetTypeCode.WIND:
          return weatherStationSamplePointReducer(acc, rawSP, existingSamplePointsState);
        case RawAssetTypeCode.HEATER_SENSOR:
          return heaterSamplePointsReducer(acc, rawSP);
        default: {
          if (rawSP.sensorTags.id === SensorTagId.DAVIS_WEATHER_STATION_RAIN_AU
            || rawSP.sensorTags.id === SensorTagId.DAVIS_WEATHER_STATION_RAIN_US
            || rawSP.sensorTags.id === SensorTagId.SPECTRUM_WEATHER_STATION_RAIN) {
            return weatherStationSamplePointReducer(acc, rawSP, existingSamplePointsState);
          }
          const parsedSP = parseRawSamplePoint(rawSP, country);
          if (!parsedSP) return acc;
          return [...acc, parsedSP];
        }
      }
    }, []);
};
